import {
  Component,
  Directive,
  ElementRef,
  OnDestroy,
  inject,
  input,
} from '@angular/core';
import { ConnectedPosition, Overlay, OverlayRef } from '@angular/cdk/overlay';
import { ComponentPortal } from '@angular/cdk/portal';

@Component({
  selector: 'ui-tooltip-panel',
  standalone: true,
  template: `{{ text() }}`,
  styles: [`
    :host {
      display: block;
      max-width: 16rem;
      padding: 0.375rem 0.625rem;
      border-radius: 0.375rem;
      background: var(--tooltip-bg, #1f2937);
      color: var(--tooltip-fg, #fff);
      font-size: 0.8125rem;
      font-weight: 500;
      line-height: 1.25;
      white-space: nowrap;
      box-shadow:
        0 4px 12px rgb(15 23 42 / 16%),
        0 1px 2px rgb(15 23 42 / 8%);
      pointer-events: none;
    }
  `],
})
export class UiTooltipPanelComponent {
  text = input('');
}

const TOOLTIP_POSITIONS: Record<string, ConnectedPosition[]> = {
  right: [
    {
      originX: 'end',
      originY: 'center',
      overlayX: 'start',
      overlayY: 'center',
      offsetX: 8,
    },
    {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -8,
    },
  ],
  left: [
    {
      originX: 'start',
      originY: 'center',
      overlayX: 'end',
      overlayY: 'center',
      offsetX: -8,
    },
  ],
  top: [
    {
      originX: 'center',
      originY: 'top',
      overlayX: 'center',
      overlayY: 'bottom',
      offsetY: -8,
    },
  ],
  bottom: [
    {
      originX: 'center',
      originY: 'bottom',
      overlayX: 'center',
      overlayY: 'top',
      offsetY: 8,
    },
  ],
};

@Directive({
  selector: '[uiTooltip]',
  standalone: true,
  host: {
    '(mouseenter)': 'onShow()',
    '(mouseleave)': 'onHide()',
    '(focus)': 'onShow()',
    '(blur)': 'onHide()',
  },
})
export class UiTooltipDirective implements OnDestroy {
  private overlay = inject(Overlay);
  private host = inject(ElementRef<HTMLElement>);

  private overlayRef: OverlayRef | null = null;
  private showTimer: ReturnType<typeof setTimeout> | null = null;

  uiTooltip = input<string | null | undefined>(null);
  uiTooltipDisabled = input(false);
  uiTooltipPosition = input<'right' | 'left' | 'top' | 'bottom'>('right');
  uiTooltipDelay = input(120);

  ngOnDestroy(): void {
    this.clearShowTimer();
    this.detach();
  }

  onShow(): void {
    if (this.uiTooltipDisabled() || !this.uiTooltip()?.trim()) {
      return;
    }

    this.clearShowTimer();
    const delay = this.uiTooltipDelay();
    if (delay <= 0) {
      this.attach();
      return;
    }

    this.showTimer = setTimeout(() => this.attach(), delay);
  }

  onHide(): void {
    this.clearShowTimer();
    this.detach();
  }

  private attach(): void {
    const text = this.uiTooltip()?.trim();
    if (!text || this.overlayRef) {
      return;
    }

    const positions = TOOLTIP_POSITIONS[this.uiTooltipPosition()] ?? TOOLTIP_POSITIONS['right'];
    this.overlayRef = this.overlay.create({
      positionStrategy: this.overlay
        .position()
        .flexibleConnectedTo(this.host)
        .withPositions(positions)
        .withPush(false),
      scrollStrategy: this.overlay.scrollStrategies.reposition(),
      panelClass: 'ui-tooltip-overlay-panel',
    });

    const portalRef = this.overlayRef.attach(new ComponentPortal(UiTooltipPanelComponent));
    portalRef.setInput('text', text);
  }

  private detach(): void {
    this.overlayRef?.dispose();
    this.overlayRef = null;
  }

  private clearShowTimer(): void {
    if (this.showTimer) {
      clearTimeout(this.showTimer);
      this.showTimer = null;
    }
  }
}
